/**
 * Gate de 2º factor (F-12). Decisión pura compartida por los `beforeLoad` de
 * `_authenticated/_layout` y `/login`: tras NEW_PASSWORD el user ya tiene tokens
 * (no hay challenge de Cognito que fuerce el MFA), así que el FRONT es la única
 * barrera. Mientras no haya elegido 2º factor (`mfaSetupRequired`), no lo dejamos
 * entrar a la app y lo mandamos a la pantalla de elección.
 */

import { redirect } from '@tanstack/react-router';
import type { AuthUser } from '../stores/authStore';

/** Ruta de la pantalla de elección de 2º factor (post-token, self-service). */
export const MFA_SETUP_PATH = '/me/security/mfa-setup';

/** Prefijo de las rutas donde el user SÍ puede estar mientras completa la elección. */
export const MFA_SETUP_AREA = '/me/security';

/**
 * Devuelve la ruta a la que hay que redirigir por falta de 2º factor, o `null`
 * si el acceso está permitido. No redirige si el user ya está dentro de
 * `/me/security/*` (ahí completa la elección sin rebotar en loop).
 */
export function resolveMfaSetupRedirect(
    user: { mfaSetupRequired: boolean },
    currentPathname: string,
): string | null {
    if (!user.mfaSetupRequired) return null;
    if (currentPathname.startsWith(MFA_SETUP_AREA)) return null;
    return MFA_SETUP_PATH;
}

/** Lo mínimo del estado de auth que necesitan los guards. */
export interface SessionState {
    user: AuthUser | null;
    error: Error | null;
}

export interface SessionSource<S extends SessionState = SessionState> {
    ensureLoaded: () => Promise<S>;
}

/**
 * Guard de sesión compartido por los `beforeLoad`: sin `user` ni `error` la sesión venció y va a
 * /login. Un error al leer el perfil no es sesión vencida: deja pasar para que la pantalla ofrezca reintentar.
 */
export async function requireSession(auth: SessionSource, pathname: string): Promise<void> {
    const state = await auth.ensureLoaded();
    if (!state.user) {
        if (state.error) return;
        throw redirect({ to: '/login', search: { from: pathname } });
    }
    const mfaRedirect = resolveMfaSetupRedirect(state.user, pathname);
    if (mfaRedirect) {
        throw redirect({ to: mfaRedirect });
    }
}

export type AccessState =
    | { kind: 'loading'; permissions: ReadonlySet<string> }
    | { kind: 'error'; permissions: ReadonlySet<string> }
    | { kind: 'ready'; permissions: ReadonlySet<string> };

/** Estado del perfil para decidir portales: solo `ready` permite concluir que falta un permiso. */
export function resolveAccess(
    state: SessionState & { permissions: ReadonlySet<string> },
): AccessState {
    const { permissions } = state;
    if (state.user) return { kind: 'ready', permissions };
    if (state.error) return { kind: 'error', permissions };
    return { kind: 'loading', permissions };
}
